
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface TimeSelectorProps {
  selectedTime: string | undefined;
  onTimeChange: (value: string) => void;
  selectedDate: Date | undefined;
  parkingSpotId: string;
}

const TimeSelector = ({ selectedTime, onTimeChange, selectedDate, parkingSpotId }: TimeSelectorProps) => {
  const [bookedTimeSlots, setBookedTimeSlots] = useState<string[]>([]);

  useEffect(() => {
    const fetchBookedTimeSlots = async () => {
      if (!selectedDate || !parkingSpotId) return;

      // Set the start and end of the selected date
      const startOfDay = new Date(selectedDate);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(selectedDate);
      endOfDay.setHours(23, 59, 59, 999);

      const { data: bookings, error } = await supabase
        .from('bookings')
        .select('start_time, end_time')
        .eq('parking_spot_id', parkingSpotId)
        .eq('status', 'confirmed') // Only consider confirmed bookings
        .gte('start_time', startOfDay.toISOString())
        .lte('start_time', endOfDay.toISOString());

      if (error) {
        console.error('Error fetching bookings:', error);
        return;
      }

      console.log('Fetched bookings:', bookings); // Debug log

      // Extract all booked time slots between start and end times
      const bookedSlots = new Set<string>();
      bookings?.forEach(booking => {
        const startTime = new Date(booking.start_time);
        const endTime = new Date(booking.end_time);
        
        // Add all time slots between start and end time
        for (let time = new Date(startTime); time < endTime; time.setMinutes(time.getMinutes() + 1)) {
          const hours = time.getHours().toString().padStart(2, '0');
          const minutes = time.getMinutes().toString().padStart(2, '0');
          bookedSlots.add(`${hours}:${minutes}`);
        }
      });

      console.log('Booked slots sample:', Array.from(bookedSlots).slice(0, 10)); // Debug log sample
      setBookedTimeSlots(Array.from(bookedSlots));
    };

    fetchBookedTimeSlots();
  }, [selectedDate, parkingSpotId]);

  // Check if a time slot is in the past
  const isTimeSlotInPast = (timeSlot: string): boolean => {
    if (!selectedDate) return false;
    
    const now = new Date();
    const selectedDateObj = new Date(selectedDate);
    
    // If selected date is in the future, no time slots are in the past
    if (selectedDateObj.getDate() !== now.getDate() || 
        selectedDateObj.getMonth() !== now.getMonth() || 
        selectedDateObj.getFullYear() !== now.getFullYear()) {
      return false;
    }
    
    // For today, check if the time slot is in the past
    const [hoursStr, minutesStr] = timeSlot.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    
    if (hours < now.getHours()) {
      return true;
    } else if (hours === now.getHours() && minutes <= now.getMinutes()) {
      return true;
    }
    
    return false;
  };

  // Format time for display (12-hour format with AM/PM)
  const formatTime = (hours: number, minutes: number): string => {
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 === 0 ? 12 : hours % 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Generate time slots in 5-minute intervals
  const generateTimeSlots = () => {
    const slots = [];
    
    for (let i = 0; i < 24; i++) {
      for (let j = 0; j < 60; j += 5) { // 5-minute intervals for better UI performance
        const hour = i.toString().padStart(2, "0");
        const minute = j.toString().padStart(2, "0");
        const timeSlot = `${hour}:${minute}`;
        
        // Check if this time is booked or in the past
        const isBooked = bookedTimeSlots.some(bookedSlot => {
          const [bookedHour, bookedMinute] = bookedSlot.split(':').map(Number);
          return i === bookedHour && Math.abs(j - bookedMinute) < 5; // Consider booked if within 5 minutes
        });
        
        const isPast = isTimeSlotInPast(timeSlot);
        const isDisabled = isBooked || isPast;
        
        let disabledReason = '';
        if (isBooked) disabledReason = ' (Booked)';
        else if (isPast) disabledReason = ' (Past)';

        // Store the 24-hour format as value, but display in 12-hour format
        slots.push({
          value: timeSlot, // 24-hour format (HH:MM) for backend
          label: formatTime(i, j) + disabledReason, // 12-hour format for display
          disabled: isDisabled
        });
      }
    }
    
    return slots;
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium">Select Time</label>
      <Select onValueChange={onTimeChange} value={selectedTime}>
        <SelectTrigger>
          <SelectValue placeholder="Select time" />
        </SelectTrigger>
        <SelectContent className="max-h-[300px]">
          {generateTimeSlots().map(slot => (
            <SelectItem 
              key={slot.value} 
              value={slot.value}
              disabled={slot.disabled}
            >
              {slot.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default TimeSelector;
